import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Ban,
  Building2,
  CalendarDays,
  CheckCircle2,
  HelpCircle,
  Filter,
  KeyRound,
  MessageSquare,
  Package,
  Pencil,
  Printer,
  Save,
  Search,
  Send,
  Settings,
  Smartphone,
  Store,
  Tag,
  Target,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import DeviceHealthBadge from '../components/DeviceHealthBadge';
import HealthStatusLegend from '../components/HealthStatusLegend';
import Pagination from '../components/Pagination';
import TableHeader from '../components/TableHeader';
import TableZoomControls, { useTableZoom } from '../components/TableZoomControls';
import apiService from '../services/apiService';
import './AllDevices.css';

// NOTE: We intentionally cap the list for performance.
// TODO(future): migrate AllDevices to server-side pagination + filtering using `GET /api/admin/devices/all-with-filters`
// and add a backend bulk health endpoint (single call) to avoid N requests per page.
const MAX_DEVICES = 5000;
const HEALTH_PRIORITY = {
  HEALTHY: 0,
  DEGRADED: 1,
  WARNING: 2,
  ERROR: 3,
  OFFLINE: 4,
  UNKNOWN: 5,
};
const DEVICE_STATUS_PRIORITY = {
  ACTIVE: 0,
  SHIPPED_PENDING_ACTIVATION: 1,
  PROVISIONED: 2,
  DISABLED: 3,
};
const ASSIGNMENT_TYPE_LABELS = {
  GLOBAL_POOL: 'Pool globale',
  TEAM: 'Team specifico',
  SINGLE_WORKER: 'Singolo percettore',
};

const DeviceColumnLabel = ({ icon: Icon, children }) => (
  <span className="device-column-label">
    <Icon size={14} aria-hidden="true" />
    <span>{children}</span>
  </span>
);

const AllDevices = () => {
  const tableZoom = useTableZoom();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNotes, setEditingNotes] = useState({});

  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    assignmentType: '',
    hasHealth: ''
  });
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('health');
  const [sortDirection, setSortDirection] = useState('asc');

  // Device health state
  const [devicesHealth, setDevicesHealth] = useState({});
  const [, setLoadingHealth] = useState(false);

  // WebSocket state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    title: '',
    message: '',
    type: 'info',
    duration: 0,
    dismissible: true
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  const loadDevicesHealth = useCallback(async (deviceList) => {
    setLoadingHealth(true);
    const activeDevices = deviceList.filter(d => d.status === 'ACTIVE');
    const deviceIds = activeDevices.map(d => d.id);

    if (deviceIds.length === 0) {
      setLoadingHealth(false);
      return;
    }

    try {
      const healthMap = await apiService.getBulkDevicesHealth(deviceIds);
      setDevicesHealth(prev => ({
        ...prev,
        ...healthMap
      }));
    } catch (err) {
      console.error('Error loading devices health:', err);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllDevices({ limit: MAX_DEVICES, offset: 0 });
      setDevices(response);
      await loadDevicesHealth(response);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [loadDevicesHealth]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Reset to first page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery]);

  const handleOpenMessageModal = (device) => {
    setSelectedDevice(device);
    setMessageForm({
      title: '',
      message: '',
      type: 'info',
      duration: 0,
      dismissible: true
    });
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedDevice(null);
  };

  const handleSendMessage = async () => {
    if (!messageForm.title || !messageForm.message) {
      alert('Titolo e messaggio sono obbligatori');
      return;
    }

    try {
      setSendingMessage(true);
      const result = await apiService.sendDisplayMessage(selectedDevice.id, messageForm);

      if (result.success) {
        alert(`Messaggio inviato con successo a ${selectedDevice.label}`);
        handleCloseMessageModal();
      } else {
        alert(`Errore: ${result.error}`);
      }
    } catch (err) {
      alert(`Errore nell'invio: ${err.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredDevices = useMemo(() => {
    let filtered = devices;

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(device => device.status === filters.status);
    }

    // Filter by tip assignment mode
    if (filters.assignmentType) {
      filtered = filtered.filter(device => device.assignment_type === filters.assignmentType);
    }

    // Filter by health status
    if (filters.hasHealth === 'online') {
      filtered = filtered.filter(device =>
        device.status === 'ACTIVE' && devicesHealth[device.id]?.overallHealth === 'HEALTHY'
      );
    } else if (filters.hasHealth === 'offline') {
      filtered = filtered.filter(device =>
        device.status === 'ACTIVE' && devicesHealth[device.id]?.overallHealth !== 'HEALTHY'
      );
    }

    // Filter by search query (setup code, label, tenant name, venue name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(device =>
        (device.setupCode || '').toLowerCase().includes(query) ||
        (device.label || '').toLowerCase().includes(query) ||
        (device.tenant?.adminAlias || '').toLowerCase().includes(query) ||
        (device.tenant?.company_name || '').toLowerCase().includes(query) ||
        (device.venue?.name || '').toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aVal, bVal;

        switch (sortColumn) {
          case 'tenant':
            aVal = (a.tenant?.adminAlias || a.tenant?.company_name || '').toLowerCase();
            bVal = (b.tenant?.adminAlias || b.tenant?.company_name || '').toLowerCase();
            break;
          case 'venue':
            aVal = (a.venue?.name || '').toLowerCase();
            bVal = (b.venue?.name || '').toLowerCase();
            break;
          case 'label':
            aVal = (a.label || '').toLowerCase();
            bVal = (b.label || '').toLowerCase();
            break;
          case 'setupCode':
            aVal = (a.setupCode || '').toLowerCase();
            bVal = (b.setupCode || '').toLowerCase();
            break;
          case 'assignmentType':
            aVal = (ASSIGNMENT_TYPE_LABELS[a.assignment_type] || a.assignment_type || '').toLowerCase();
            bVal = (ASSIGNMENT_TYPE_LABELS[b.assignment_type] || b.assignment_type || '').toLowerCase();
            break;
          case 'status':
            aVal = DEVICE_STATUS_PRIORITY[a.status] ?? 99;
            bVal = DEVICE_STATUS_PRIORITY[b.status] ?? 99;
            break;
          case 'health':
            aVal = a.status === 'ACTIVE'
              ? HEALTH_PRIORITY[devicesHealth[a.id]?.overallHealth || 'UNKNOWN']
              : 6;
            bVal = b.status === 'ACTIVE'
              ? HEALTH_PRIORITY[devicesHealth[b.id]?.overallHealth || 'UNKNOWN']
              : 6;
            break;
          case 'createdAt':
            aVal = new Date(a.createdAt || 0).getTime();
            bVal = new Date(b.createdAt || 0).getTime();
            break;
          default:
            return 0;
        }

        // Compare values
        if (typeof aVal === 'string') {
          const comparison = aVal.localeCompare(bVal);
          return sortDirection === 'asc' ? comparison : -comparison;
        } else {
          if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
          return (a.label || '').localeCompare(b.label || '');
        }
      });
    }

    return filtered;
  }, [devices, filters, searchQuery, sortColumn, sortDirection, devicesHealth]);

  const paginatedDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDevices.slice(startIndex, endIndex);
  }, [filteredDevices, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  const handleStatusChange = async (deviceId, newStatus) => {
    if (!window.confirm(`Sei sicuro di voler cambiare lo status in "${getStatusLabel(newStatus)}"?`)) return;
    try {
      await apiService.updateDeviceStatus(deviceId, newStatus);

      // Update the device locally instead of reloading all data
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device.id === deviceId ? { ...device, status: newStatus } : device
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo totem?')) return;
    try {
      await apiService.deleteDevice(deviceId);

      // Remove the device locally instead of reloading all data
      setDevices(prevDevices =>
        prevDevices.filter(device => device.id !== deviceId)
      );
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Failed to delete device');
    }
  };

  const handleNotesChange = useCallback((deviceId, notes) => {
    setEditingNotes(prev => ({
      ...prev,
      [deviceId]: notes
    }));
  }, []);

  const handleEditDevice = useCallback((device) => {
    setEditFormData({
      id: device.id,
      label: device.label || '',
      setupCode: device.setupCode || '',
      assignment_type: device.assignment_type || 'GLOBAL_POOL',
      notes: device.notes || '',
      status: device.status || 'PROVISIONED'
    });
    setShowEditModal(true);
  }, []);

  const handleSaveEdit = async () => {
    try {
      await apiService.updateDevice(editFormData.id, {
        label: editFormData.label,
        mac_address: editFormData.mac_address,
        assignment_type: editFormData.assignment_type,
        notes: editFormData.notes,
        status: editFormData.status
      });

      // Update the device locally
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device.id === editFormData.id ? { ...device, ...editFormData } : device
        )
      );

      setShowEditModal(false);
      setEditFormData({});
    } catch (err) {
      console.error('Error updating device:', err);
      alert('Failed to update device');
    }
  };

  const handleSaveNotes = async (deviceId) => {
    try {
      const notes = editingNotes[deviceId];
      const response = await apiService.updateDeviceNotes(deviceId, notes);

      console.log('Notes updated successfully:', response);

      // Update the device locally instead of reloading all data
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device.id === deviceId ? { ...device, notes } : device
        )
      );

      setEditingNotes(prev => {
        const updated = { ...prev };
        delete updated[deviceId];
        return updated;
      });
    } catch (err) {
      console.error('Error updating notes:', err);
      console.error('Error details:', err.response?.data || err.message);
      alert(`Failed to update notes: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePrintLabel = async (device) => {
    setSelectedDevice(device);
    try {
      const qrDataUrl = await QRCode.toDataURL(device.setupCode, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setQrCodeDataUrl('');
    }
    setShowLabelModal(true);
  };

  const handlePrintLabelAction = () => {
    if (!selectedDevice || !qrCodeDataUrl) return;

    // Create a new window with only the label content
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Etichetta ${selectedDevice.label}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              text-align: center;
              background: white;
            }
            .label-container {
              max-width: 300px;
              margin: 0 auto;
              padding: 20px;
              border: 1px solid #ddd;
            }
            h3 {
              margin: 0 0 10px 0;
              font-size: 18px;
              color: #333;
            }
            .device-info {
              margin-bottom: 20px;
              font-size: 12px;
              color: #666;
            }
            .qr-container {
              margin: 20px 0;
            }
            img {
              width: 150px;
              height: 150px;
              border: 1px solid #ddd;
            }
            .setup-code {
              margin-top: 15px;
            }
            .setup-code p:first-child {
              font-size: 10px;
              color: #666;
              margin: 0 0 5px 0;
            }
            .setup-code p:last-child {
              font-size: 16px;
              font-family: 'Courier New', monospace;
              font-weight: bold;
              margin: 0;
              background: #f5f5f5;
              padding: 8px;
              border: 1px solid #ddd;
              display: inline-block;
            }
            @media print {
              body { margin: 0; padding: 10px; }
              .label-container { border: none; padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="label-container">
            <h3>Etichetta Totem</h3>
            <div class="device-info">
              <strong>${selectedDevice.label}</strong><br>
              ${selectedDevice.tenant?.company_name || 'N/A'} - ${selectedDevice.venue?.name || 'N/A'}
            </div>
            <div class="qr-container">
              <img src="${qrCodeDataUrl}" alt="QR Code Setup" />
              <div class="setup-code">
                <p>Setup Code:</p>
                <p>${selectedDevice.setupCode}</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Wait for image to load, then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Helpers status device
  const getStatusIcon = (status) => {
    switch (status) {
      case 'PROVISIONED': return <Package size={14} aria-hidden="true" />;
      case 'SHIPPED_PENDING_ACTIVATION': return <Truck size={14} aria-hidden="true" />;
      case 'ACTIVE': return <CheckCircle2 size={14} aria-hidden="true" />;
      case 'DISABLED': return <Ban size={14} aria-hidden="true" />;
      default: return <HelpCircle size={14} aria-hidden="true" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PROVISIONED': return 'Provvisionato';
      case 'SHIPPED_PENDING_ACTIVATION': return 'Spedito';
      case 'ACTIVE': return 'Attivo';
      case 'DISABLED': return 'Disabilitato';
      default: return status || 'N/A';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PROVISIONED': return '#ffa500';
      case 'SHIPPED_PENDING_ACTIVATION': return '#17a2b8';
      case 'ACTIVE': return '#28a745';
      case 'DISABLED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) return <div className="loading">Caricamento totem...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="devices-page">
      <header className="devices-page-header">
        <span className="devices-page-header-icon">
          <Smartphone size={21} aria-hidden="true" />
        </span>
        <div>
          <h1>Gestione devices</h1>
          <p>Visualizza dispositivi, stato operativo e health in tempo reale.</p>
          <small>Visualizzazione limitata a {MAX_DEVICES} dispositivi. Gli HEALTHY sono mostrati per primi.</small>
          {error && <div className="error">{error}</div>}
        </div>
      </header>

      <DeviceFilterBar
        onFilterChange={handleFilterChange}
        totalCount={devices.length}
        filteredCount={filteredDevices.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Contenuto Devices */}
      <div className="card table-scroll-card">
        <TableZoomControls {...tableZoom} />
        <div className="table-container table-scroll-frame">
          <table
            className="admin-table devices-table mobile-zoom-table"
            style={{ '--mobile-table-zoom': `${tableZoom.zoom}%` }}
          >
            <TableHeader
              columns={[
                { key: 'tenant', label: <DeviceColumnLabel icon={Building2}>Alias/Tenant</DeviceColumnLabel>, sortable: true, width: '110px' },
                { key: 'venue', label: <DeviceColumnLabel icon={Store}>Locale</DeviceColumnLabel>, sortable: true, width: '110px' },
                { key: 'label', label: <DeviceColumnLabel icon={Smartphone}>Nome Totem</DeviceColumnLabel>, sortable: true, width: '90px' },
                { key: 'setupCode', label: <DeviceColumnLabel icon={KeyRound}>Setup Code</DeviceColumnLabel>, sortable: true, width: '90px' },
                { key: 'assignmentType', label: <DeviceColumnLabel icon={Target}>Assegnazione</DeviceColumnLabel>, sortable: true, width: '90px' },
                { key: 'status', label: <DeviceColumnLabel icon={Activity}>Stato</DeviceColumnLabel>, sortable: true, width: '90px' },
                { key: 'health', label: <DeviceColumnLabel icon={Activity}>Health</DeviceColumnLabel>, sortable: true, width: '100px' },
                { key: 'notes', label: <DeviceColumnLabel icon={Pencil}>Note</DeviceColumnLabel>, sortable: false, width: '160px' },
                { key: 'createdAt', label: <DeviceColumnLabel icon={CalendarDays}>Creato</DeviceColumnLabel>, sortable: true, width: '70px' },
                { key: 'actions', label: <DeviceColumnLabel icon={Settings}>Azioni</DeviceColumnLabel>, sortable: false, width: '220px' }
              ]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <tbody>
              {paginatedDevices.map(device => (
                <tr key={device.id}>
                  <td style={{
                    width: '110px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '13px'
                  }}>
                    {device.tenant?.adminAlias ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: '#495057', fontStyle: 'italic', fontWeight: 700, fontSize: '12px' }}>{device.tenant.adminAlias}</span>
                        <strong>{device.tenant?.company_name || 'N/A'}</strong>
                      </div>
                    ) : (
                      <strong>{device.tenant?.company_name || 'N/A'}</strong>
                    )}
                  </td>
                  <td style={{
                    width: '110px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '13px'
                  }}>
                    <strong>{device.venue?.name || 'N/A'}</strong>
                  </td>
                  <td style={{
                    width: '90px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '13px'
                  }}>
                    <strong>{device.label}</strong>
                  </td>
                  <td style={{
                    width: '90px',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                  }}>
                    <code style={{
                      backgroundColor: '#f5f5f5',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      color: '#333'
                    }}>
                      {device.setupCode || 'N/A'}
                    </code>
                  </td>
                  <td style={{
                    width: '90px',
                    fontSize: '10px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                    {ASSIGNMENT_TYPE_LABELS[device.assignment_type] || device.assignment_type || 'N/A'}
                  </td>
                  <td style={{ width: '90px', fontSize: '11px' }}>
                    <span className="device-lifecycle-status" style={{ color: getStatusColor(device.status) }}>
                      {getStatusIcon(device.status)} {getStatusLabel(device.status)}
                    </span>
                  </td>
                  <td style={{ width: '100px', textAlign: 'center' }}>
                    {device.status === 'ACTIVE' && (
                      <DeviceHealthBadge
                        health={devicesHealth[device.id]}
                        onClick={() => window.open(`/devices/${device.id}/health`, '_blank')}
                        showLabel
                      />
                    )}
                  </td>
                  <td style={{ width: '160px' }}>
                    <textarea
                      value={editingNotes[device.id] !== undefined ? editingNotes[device.id] : (device.notes || '')}
                      onChange={(e) => handleNotesChange(device.id, e.target.value)}
                      placeholder="Aggiungi note..."
                      rows={2}
                      style={{
                        width: '100%',
                        minHeight: '35px',
                        resize: 'vertical',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '4px 6px',
                        fontSize: '11px'
                      }}
                    />
                    {editingNotes[device.id] !== undefined && (
                      <div style={{ marginTop: '4px' }}>
                        <button
                          className="btn btn-small"
                          onClick={() => handleSaveNotes(device.id)}
                          style={{ fontSize: '10px', padding: '2px 4px' }}
                        >
                          <span className="device-inline-label"><Save size={12} aria-hidden="true" /> Salva</span>
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{
                    width: '70px',
                    fontSize: '10px',
                    whiteSpace: 'nowrap'
                  }}>
                    {new Date(device.createdAt || Date.now()).toLocaleDateString()}
                  </td>
                  <td style={{ width: '180px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '2px',
                      alignItems: 'center',
                      flexWrap: 'nowrap'
                    }}>
                      <button
                        className="btn btn-mini btn-secondary"
                        onClick={() => handleEditDevice(device)}
                        title="Modifica Device"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>

                      <button
                        className="btn btn-mini"
                        style={{ backgroundColor: '#6f42c1', color: 'white' }}
                        onClick={() => handlePrintLabel(device)}
                        title="Visualizza e Stampa Etichetta"
                      >
                        <Tag size={14} aria-hidden="true" />
                      </button>

                      {/* Quick status buttons */}
                      {device.status === 'PROVISIONED' && (
                        <button
                          className="btn btn-mini"
                          style={{ backgroundColor: '#17a2b8', color: 'white' }}
                          onClick={() => handleStatusChange(device.id, 'SHIPPED_PENDING_ACTIVATION')}
                          title="Spedisci Totem"
                        >
                          <Truck size={14} aria-hidden="true" />
                        </button>
                      )}
                      {device.status === 'SHIPPED_PENDING_ACTIVATION' && (
                        <button
                          className="btn btn-mini"
                          style={{ backgroundColor: '#28a745', color: 'white' }}
                          onClick={() => handleStatusChange(device.id, 'ACTIVE')}
                          title="Attiva Totem"
                        >
                          <CheckCircle2 size={14} aria-hidden="true" />
                        </button>
                      )}
                      {device.status === 'ACTIVE' && (
                        <button
                          className="btn btn-mini"
                          style={{ backgroundColor: '#dc3545', color: 'white' }}
                          onClick={() => handleStatusChange(device.id, 'DISABLED')}
                          title="Disabilita Totem"
                        >
                          <Ban size={14} aria-hidden="true" />
                        </button>
                      )}
                      {device.status === 'DISABLED' && (
                        <button
                          className="btn btn-mini"
                          style={{ backgroundColor: '#28a745', color: 'white' }}
                          onClick={() => handleStatusChange(device.id, 'ACTIVE')}
                          title="Riattiva Totem"
                        >
                          <CheckCircle2 size={14} aria-hidden="true" />
                        </button>
                      )}

                      {/* Send Message Button - Only for ACTIVE + WebSocket ONLINE */}
                      {device.status === 'ACTIVE' &&
                        devicesHealth[device.id]?.websocket?.status === 'ONLINE' && (
                          <button
                            className="btn btn-mini"
                            style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            onClick={() => handleOpenMessageModal(device)}
                            title="Invia Messaggio Display (WebSocket ONLINE)"
                          >
                            <MessageSquare size={14} aria-hidden="true" />
                          </button>
                        )}

                      <button
                        className="btn btn-mini btn-danger"
                        onClick={() => handleDeleteDevice(device.id)}
                        title="Elimina Totem"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredDevices.length === 0 && !loading && (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            Nessun totem trovato con i filtri selezionati.
          </p>
        )}

        {filteredDevices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredDevices.length / itemsPerPage)}
            totalItems={filteredDevices.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Modal per Modifica Device */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3><span className="device-inline-label"><Pencil size={18} aria-hidden="true" /> Modifica device</span></h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="device-inline-label"><Smartphone size={14} aria-hidden="true" /> Nome device</label>
                <input
                  type="text"
                  value={editFormData.label || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Es: Totem Ingresso"
                />
              </div>

              <div className="form-group">
                <label className="device-inline-label"><KeyRound size={14} aria-hidden="true" /> Setup code</label>
                <input
                  type="text"
                  value={editFormData.setupCode || ''}
                  readOnly
                  placeholder="Setup code (non modificabile)"
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group">
                <label className="device-inline-label"><Target size={14} aria-hidden="true" /> Tipo assegnazione</label>
                <select
                  value={editFormData.assignment_type || 'GLOBAL_POOL'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, assignment_type: e.target.value }))}
                >
                  <option value="GLOBAL_POOL">GLOBAL_POOL</option>
                  <option value="TEAM">TEAM</option>
                  <option value="SINGLE_WORKER">SINGLE_WORKER</option>
                </select>
              </div>

              <div className="form-group">
                <label className="device-inline-label"><Activity size={14} aria-hidden="true" /> Stato</label>
                <select
                  value={editFormData.status || 'PROVISIONED'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="PROVISIONED">Provvisionato</option>
                  <option value="SHIPPED_PENDING_ACTIVATION">Spedito</option>
                  <option value="ACTIVE">Attivo</option>
                  <option value="DISABLED">Disabilitato</option>
                </select>
              </div>

              <div className="form-group">
                <label className="device-inline-label"><Pencil size={14} aria-hidden="true" /> Note</label>
                <textarea
                  value={editFormData.notes || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Aggiungi note..."
                  rows={3}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  <span className="device-inline-label"><X size={15} aria-hidden="true" /> Annulla</span>
                </button>
                <button
                  className="btn"
                  onClick={handleSaveEdit}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  <span className="device-inline-label"><Save size={15} aria-hidden="true" /> Salva modifiche</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal per Etichetta QR */}
      {showLabelModal && selectedDevice && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px', textAlign: 'center' }}>
            <div className="modal-header">
              <h3><span className="device-inline-label"><Tag size={18} aria-hidden="true" /> Etichetta totem</span></h3>
              <button
                className="modal-close"
                onClick={() => setShowLabelModal(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '30px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h4><span className="device-inline-label"><Smartphone size={15} aria-hidden="true" /> {selectedDevice.label}</span></h4>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  {selectedDevice.tenant?.company_name} - {selectedDevice.venue?.name}
                </p>
              </div>

              {/* QR Code */}
              <div style={{
                border: '2px dashed #ddd',
                padding: '20px',
                marginBottom: '20px',
                backgroundColor: '#fafafa'
              }}>
                {qrCodeDataUrl ? (
                  <img
                    src={qrCodeDataUrl}
                    alt="QR Code Setup"
                    style={{
                      width: '200px',
                      height: '200px',
                      border: '1px solid #ddd'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '200px',
                    height: '200px',
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5'
                  }}>
                    <span style={{ color: '#666' }}>Generando QR Code...</span>
                  </div>
                )}
                <div style={{ marginTop: '15px' }}>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                    Setup Code:
                  </p>
                  <p style={{
                    fontSize: '18px',
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    backgroundColor: 'white',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    {selectedDevice.setupCode}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  className="btn"
                  onClick={handlePrintLabelAction}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  <span className="device-inline-label"><Printer size={15} aria-hidden="true" /> Stampa etichetta</span>
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowLabelModal(false)}
                >
                  <span className="device-inline-label"><X size={15} aria-hidden="true" /> Chiudi</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Invio Messaggio */}
      {showMessageModal && selectedDevice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3><span className="device-inline-label"><MessageSquare size={18} aria-hidden="true" /> Invia messaggio a {selectedDevice.label}</span></h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Titolo *
              </label>
              <input
                type="text"
                value={messageForm.title}
                onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                placeholder="Es: Stiamo chiudendo"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Messaggio *
              </label>
              <textarea
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                placeholder="Es: Il locale chiuderà tra 10 minuti"
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Tipo Messaggio
              </label>
              <select
                value={messageForm.type}
                onChange={(e) => setMessageForm({ ...messageForm, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="info">ℹ️ Info (Blu)</option>
                <option value="warning">⚠️ Warning (Arancio)</option>
                <option value="error">Error (Rosso)</option>
                <option value="success">Success (Verde)</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Durata (secondi, 0 = infinito)
              </label>
              <input
                type="number"
                value={messageForm.duration}
                onChange={(e) => setMessageForm({ ...messageForm, duration: parseInt(e.target.value) || 0 })}
                min="0"
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={messageForm.dismissible}
                  onChange={(e) => setMessageForm({ ...messageForm, dismissible: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span>Utente può chiudere il messaggio</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseMessageModal}
                disabled={sendingMessage}
                className="btn btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageForm.title || !messageForm.message}
                className="btn"
                style={{
                  backgroundColor: sendingMessage ? '#6c757d' : '#007bff',
                  color: 'white'
                }}
              >
                {sendingMessage
                  ? 'Invio...'
                  : <span className="device-inline-label"><Send size={15} aria-hidden="true" /> Invia messaggio</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Health Status Legend */}
      <HealthStatusLegend />
    </div>
  );
};

// Device FilterBar Component
const DeviceFilterBar = ({
  onFilterChange,
  totalCount,
  filteredCount,
  searchQuery,
  onSearchChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    assignmentType: '',
    hasHealth: ''
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      status: '',
      assignmentType: '',
      hasHealth: ''
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
  const hasActiveFilters = activeFilterCount > 0 || searchQuery.trim() !== '';

  const clearAllFilters = () => {
    clearFilters();
    onSearchChange('');
  };

  return (
    <div className="filter-bar">
      <div className="filter-toolbar">
        <div className="filter-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Cerca setup code, device, tenant o locale..."
            aria-label="Cerca device"
          />
          {searchQuery && (
            <button
              type="button"
              className="filter-search-clear"
              onClick={() => onSearchChange('')}
              title="Cancella ricerca"
              aria-label="Cancella ricerca"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <button
          type="button"
          className="filter-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <Filter size={16} aria-hidden="true" />
          <span>Filtri</span>
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
        </button>

        <div className="filter-results">
          <span className="result-count">
            {filteredCount} {filteredCount === 1 ? 'risultato' : 'risultati'}
            {hasActiveFilters && ` di ${totalCount}`}
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              className="clear-filters"
              onClick={clearAllFilters}
            >
              <X size={14} />
              Rimuovi filtri
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="filter-controls">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="PROVISIONED">Provvisionato</option>
              <option value="SHIPPED_PENDING_ACTIVATION">Spedito</option>
              <option value="ACTIVE">Attivo</option>
              <option value="DISABLED">Disabilitato</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Modalità assegnazione</label>
            <select
              value={filters.assignmentType}
              onChange={(e) => handleFilterChange('assignmentType', e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="GLOBAL_POOL">Pool globale</option>
              <option value="TEAM">Team specifico</option>
              <option value="SINGLE_WORKER">Singolo percettore</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Health Status</label>
            <select
              value={filters.hasHealth}
              onChange={(e) => handleFilterChange('hasHealth', e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="online">Online (Healthy)</option>
              <option value="offline">Offline / non healthy</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllDevices;
